import React, { useEffect, useMemo, useState } from "react";
import {
  Badge, Button, ButtonGroup, Card, Collapse, Form, OverlayTrigger,
  ProgressBar, Spinner, Table, Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown, faAngleRight, faBolt, faBroom, faCheck, faCircleExclamation,
  faClockRotateLeft, faFileExport, faGavel, faHourglassHalf, faMoneyBillTransfer,
  faRotate, faRotateLeft, faTrash, faTriangleExclamation, faTruckFast, faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { usePaypalJobs } from "../../contexts/PaypalJobsContext";

/**
 * Production-grade history panel for PayPal background jobs.
 *
 * Features:
 * - Summary chips (queued / running / done / error / cancelled).
 * - Filter by type + status, free-text search on label / message.
 * - Live-refreshing rows (driven by `activeJobs` polling in context).
 * - Expandable row to inspect payload, error trace, and full message.
 * - Bulk "clear finished" with confirm.
 * - Relative + absolute time, monospaced job ID for support tickets.
 * - Pause-aware: a small "live" indicator shows last sync time.
 */

const TYPE_META = {
  EXPORT_PAYMENTS: { label: "Export Payments", icon: faMoneyBillTransfer, color: "primary" },
  EXPORT_REFUNDS:  { label: "Export Refunds",  icon: faRotateLeft,        color: "danger" },
  EXPORT_TRACKING: { label: "Export Tracking", icon: faTruckFast,         color: "info" },
  ADD_TRACKING:    { label: "Add Tracking",    icon: faFileExport,        color: "success" },
  SYNC_DISPUTES:   { label: "Sync Disputes",   icon: faGavel,             color: "warning" },
  EXPORT_DISPUTES: { label: "Export Disputes", icon: faGavel,             color: "warning" },
};

const STATUS_META = {
  queued:    { label: "Hàng đợi", variant: "secondary", icon: faHourglassHalf },
  running:   { label: "Đang chạy", variant: "primary",   icon: faBolt },
  done:      { label: "Hoàn tất",  variant: "success",   icon: faCheck },
  error:     { label: "Lỗi",       variant: "danger",    icon: faCircleExclamation },
  cancelled: { label: "Đã hủy",    variant: "warning",   icon: faXmark },
};

const FINISHED = ["done", "error", "cancelled"];

const fmtDuration = (start, end) => {
  if (!start) return "—";
  const e = end ? new Date(end) : new Date();
  const ms = e - new Date(start);
  if (ms < 0 || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 30) return "vừa xong";
  if (diff < 60) return `${Math.floor(diff)}s trước`;
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} ngày trước`;
  return d.toLocaleDateString();
};

const PayloadPreview = ({ payload }) => {
  if (!payload || typeof payload !== "object") return null;
  const entries = Object.entries(payload).slice(0, 8);
  if (!entries.length) return null;
  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {entries.map(([k, v]) => {
        let display;
        if (Array.isArray(v)) display = `${v.length}`;
        else if (typeof v === "object" && v !== null) display = "{…}";
        else display = String(v);
        if (display.length > 40) display = `${display.slice(0, 40)}…`;
        return (
          <Badge key={k} bg="light" text="dark" className="fw-normal border" style={{ fontSize: 11 }}>
            <span className="text-muted">{k}:</span> {display}
          </Badge>
        );
      })}
    </div>
  );
};

const PaypalJobsHistory = () => {
  const {
    historyJobs, historyLoading, fetchHistory,
    cancelJob, deleteJob, clearFinished,
    activeJobs, lastSyncAt,
  } = usePaypalJobs();

  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [clearing, setClearing] = useState(false);

  const reload = () => fetchHistory({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
  });

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, activeJobs.length]);

  // Live row state: merge the active jobs (real-time progress) into the
  // history snapshot so the user does not need to wait for a refetch to see
  // updated progress.
  const rows = useMemo(() => {
    const map = new Map();
    historyJobs.forEach((j) => map.set(String(j._id), j));
    activeJobs.forEach((j) => map.set(String(j._id), j));
    let arr = Array.from(map.values());
    arr.sort((a, b) => new Date(b.createdAt || b.queuedAt || 0) - new Date(a.createdAt || a.queuedAt || 0));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((j) =>
        (j.label || "").toLowerCase().includes(q)
        || (j.progress?.message || "").toLowerCase().includes(q)
        || (j.error || "").toLowerCase().includes(q)
        || String(j._id).toLowerCase().includes(q)
      );
    }
    return arr;
  }, [historyJobs, activeJobs, search]);

  const counts = useMemo(() => {
    const out = { queued: 0, running: 0, done: 0, error: 0, cancelled: 0 };
    rows.forEach((j) => { if (out[j.status] != null) out[j.status] += 1; });
    return out;
  }, [rows]);

  const finishedCount = counts.done + counts.error + counts.cancelled;

  const handleCancel = async (id) => {
    try { await cancelJob(id); toast.success("Đã yêu cầu hủy"); }
    catch (err) { toast.error(err.message); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Xóa job này khỏi lịch sử?")) return;
    try {
      await deleteJob(id);
      toast.success("Đã xóa");
      reload();
    } catch (err) { toast.error(err.message); }
  };
  const handleClearFinished = async () => {
    if (!finishedCount) return;
    const scope = [
      typeFilter ? `loại "${TYPE_META[typeFilter]?.label}"` : "tất cả loại",
      statusFilter && FINISHED.includes(statusFilter)
        ? `trạng thái "${STATUS_META[statusFilter]?.label}"`
        : "đã hoàn tất / lỗi / hủy",
    ].join(", ");
    if (!window.confirm(`Xóa toàn bộ job ${scope}?`)) return;
    setClearing(true);
    try {
      const filter = {};
      if (typeFilter) filter.type = typeFilter;
      if (statusFilter && FINISHED.includes(statusFilter)) filter.status = statusFilter;
      const n = await clearFinished(filter);
      toast.success(`Đã xóa ${n} job`);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClearing(false);
    }
  };

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const renderTimeCell = (j) => {
    const startIso = j.startedAt || j.queuedAt;
    if (!startIso) return <span className="text-muted">—</span>;
    const abs = new Date(startIso).toLocaleString();
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{abs}</Tooltip>}>
        <span className="small text-muted">{fmtRelative(startIso)}</span>
      </OverlayTrigger>
    );
  };

  const renderProgressCell = (j) => {
    const total = j.progress?.total || 0;
    const current = j.progress?.current || 0;
    const pct = total > 0
      ? Math.min(100, Math.round((current / total) * 100))
      : (j.status === "done" ? 100 : (j.status === "running" ? 100 : 0));
    const variant = j.status === "error" ? "danger"
      : j.status === "cancelled" ? "warning"
      : j.status === "done" ? "success"
      : undefined;
    if (j.status === "queued" && total === 0) {
      return <span className="text-muted small">Đang chờ…</span>;
    }
    return (
      <div>
        <ProgressBar
          now={pct}
          label={total > 0 ? `${pct}%` : ""}
          striped={j.status === "running" || j.status === "queued"}
          animated={j.status === "running"}
          variant={variant}
          style={{ height: 14 }}
        />
        {total > 0 && (
          <div className="small text-muted mt-1">{current.toLocaleString()} / {total.toLocaleString()}</div>
        )}
      </div>
    );
  };

  const StatChip = ({ statusKey, count }) => {
    const meta = STATUS_META[statusKey];
    const active = statusFilter === statusKey;
    return (
      <Button
        size="sm"
        variant={active ? meta.variant : `outline-${meta.variant}`}
        onClick={() => setStatusFilter(active ? "" : statusKey)}
        className="d-inline-flex align-items-center gap-1"
        aria-pressed={active}
        title={`Lọc theo ${meta.label}`}
      >
        <FontAwesomeIcon icon={meta.icon} />
        <span>{meta.label}</span>
        <Badge bg={active ? "light" : meta.variant} text={active ? "dark" : "light"}>
          {count}
        </Badge>
      </Button>
    );
  };

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <FontAwesomeIcon icon={faClockRotateLeft} className="text-secondary" />
          <strong className="me-1">Background Jobs</strong>
          <span className="text-muted small">
            {rows.length} mục
            {lastSyncAt && (
              <>
                <span className="mx-2">•</span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span
                    className="d-inline-block rounded-circle"
                    style={{ width: 8, height: 8, background: counts.running ? "#198754" : "#adb5bd" }}
                  />
                  Cập nhật {fmtRelative(lastSyncAt)}
                </span>
              </>
            )}
          </span>

          <div className="ms-auto d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              size="sm"
              type="search"
              placeholder="Tìm theo label / thông điệp / ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
            <Form.Select
              size="sm"
              style={{ width: 160 }}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Lọc theo loại"
            >
              <option value="">Tất cả loại</option>
              {Object.entries(TYPE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </Form.Select>
            <ButtonGroup size="sm">
              <Button
                variant="outline-secondary"
                onClick={reload}
                disabled={historyLoading}
                title="Tải lại"
              >
                {historyLoading
                  ? <Spinner size="sm" animation="border" />
                  : <FontAwesomeIcon icon={faRotate} />}
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleClearFinished}
                disabled={clearing || !finishedCount}
                title="Xóa các job đã kết thúc"
              >
                {clearing
                  ? <Spinner size="sm" animation="border" />
                  : <><FontAwesomeIcon icon={faBroom} className="me-1" /> Dọn ({finishedCount})</>}
              </Button>
            </ButtonGroup>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3">
          {Object.keys(STATUS_META).map((k) => (
            <StatChip key={k} statusKey={k} count={counts[k]} />
          ))}
          {(statusFilter || typeFilter || search) && (
            <Button
              size="sm"
              variant="link"
              className="text-decoration-none ms-auto"
              onClick={() => { setStatusFilter(""); setTypeFilter(""); setSearch(""); }}
            >
              Xóa bộ lọc
            </Button>
          )}
        </div>
      </Card.Header>

      <Card.Body className="bg-white">
        <div className="overflow-hidden">
          <Table size="sm" hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 28 }} aria-label="Mở rộng" />
              <th style={{ minWidth: 200 }}>Loại</th>
              <th style={{ width: 120 }}>Trạng thái</th>
              <th style={{ minWidth: 200 }}>Tiến trình</th>
              <th>Thông điệp</th>
              <th style={{ width: 130 }}>Bắt đầu</th>
              <th style={{ width: 90 }}>Thời lượng</th>
              <th style={{ width: 130 }} className="text-end">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !historyLoading && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-5">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="me-2 opacity-50" />
                  Chưa có job nào khớp bộ lọc.
                </td>
              </tr>
            )}
            {rows.map((j) => {
              const tmeta = TYPE_META[j.type] || { label: j.type, icon: faBolt, color: "secondary" };
              const smeta = STATUS_META[j.status] || { label: j.status, variant: "secondary", icon: faBolt };
              const isOpen = !!expanded[j._id];
              const isActive = j.status === "queued" || j.status === "running";
              return (
                <React.Fragment key={j._id}>
                  <tr className={isActive ? "table-active" : ""}>
                    <td className="text-muted">
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-muted"
                        onClick={() => toggleExpand(j._id)}
                        aria-label={isOpen ? "Thu gọn" : "Mở rộng"}
                      >
                        <FontAwesomeIcon icon={isOpen ? faAngleDown : faAngleRight} />
                      </Button>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={tmeta.icon} className={`text-${tmeta.color}`} />
                        <div>
                          <div className="fw-semibold">{tmeta.label}</div>
                          {j.label && <div className="small text-muted">{j.label}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge bg={smeta.variant} className="d-inline-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={smeta.icon} />
                        {smeta.label}
                      </Badge>
                    </td>
                    <td>{renderProgressCell(j)}</td>
                    <td className="small">
                      {j.error ? (
                        <span className="text-danger text-truncate d-inline-block" style={{ maxWidth: 320 }} title={j.error}>
                          {j.error}
                        </span>
                      ) : (
                        <span className="text-muted text-truncate d-inline-block" style={{ maxWidth: 320 }} title={j.progress?.message || ""}>
                          {j.progress?.message || "—"}
                        </span>
                      )}
                    </td>
                    <td>{renderTimeCell(j)}</td>
                    <td className="small text-muted">
                      {fmtDuration(j.startedAt, j.finishedAt)}
                    </td>
                    <td className="text-end">
                      {isActive && (
                        <Button size="sm" variant="outline-warning" onClick={() => handleCancel(j._id)}>
                          <FontAwesomeIcon icon={faXmark} className="me-1" />Hủy
                        </Button>
                      )}
                      {FINISHED.includes(j.status) && (
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(j._id)} title="Xóa khỏi lịch sử">
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </td>
                  </tr>
                  <tr className="border-0">
                    <td colSpan={8} className="p-0 border-0">
                      <Collapse in={isOpen}>
                        <div className="bg-white px-3 py-2 border-top">
                          <div className="row small">
                            <div className="col-md-6">
                              <div className="text-muted mb-1">Job ID</div>
                              <code className="user-select-all">{j._id}</code>
                              {j.bullJobId && (
                                <span className="ms-3 text-muted">Bull: <code>{j.bullJobId}</code></span>
                              )}
                              <div className="text-muted mt-2 mb-1">Payload</div>
                              <PayloadPreview payload={j.payload} />
                            </div>
                            <div className="col-md-6">
                              <div className="text-muted mb-1">Thời gian</div>
                              <div>Đưa vào hàng đợi: {j.queuedAt ? new Date(j.queuedAt).toLocaleString() : "—"}</div>
                              <div>Bắt đầu: {j.startedAt ? new Date(j.startedAt).toLocaleString() : "—"}</div>
                              <div>Kết thúc: {j.finishedAt ? new Date(j.finishedAt).toLocaleString() : "—"}</div>
                              {j.error && (
                                <>
                                  <div className="text-muted mt-2 mb-1">Lỗi</div>
                                  <pre className="bg-white border rounded p-2 mb-0 small text-danger" style={{ maxHeight: 160, overflow: "auto" }}>
                                    {j.error}
                                  </pre>
                                </>
                              )}
                              {j.result && (
                                <>
                                  <div className="text-muted mt-2 mb-1">Kết quả</div>
                                  <pre className="bg-white border rounded p-2 mb-0 small" style={{ maxHeight: 160, overflow: "auto" }}>
                                    {typeof j.result === "string" ? j.result : JSON.stringify(j.result, null, 2)}
                                  </pre>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default PaypalJobsHistory;
